import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaInstagram, FaWhatsapp, FaGooglePlay, FaApple } from "react-icons/fa";

const STORE_LINKS = [
  {
    name: 'play',
    href: 'https://play.google.com/store/apps/details?id=com.app.bestseed',
    badge: '/badges/google-play-badge.svg',
    label: 'Get Bestseed on Google Play',
    fallbackText: 'Get it on Google Play',
    FallbackIcon: FaGooglePlay
  },
  {
    name: 'appstore',
    href: 'https://apps.apple.com/in/app/bestseed/id6764296132',
    badge: '/badges/app-store-badge.svg',
    label: 'Download Bestseed on the App Store',
    fallbackText: 'Download on the App Store',
    FallbackIcon: FaApple
  }
];

// Renders the official store badge, falling back to an icon+text button
// if the badge asset is missing from public/badges/.
const StoreBadge = ({ store, styles }) => {
  const [failed, setFailed] = useState(false);
  const { FallbackIcon } = store;

  if (failed) {
    return (
      <span style={styles.storeBadgeFallback}>
        <FallbackIcon size={18} />
        {store.fallbackText}
      </span>
    );
  }

  return (
    <img
      src={process.env.PUBLIC_URL + store.badge}
      alt={store.label}
      style={styles.storeBadgeImg}
      onError={() => setFailed(true)}
    />
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Inline styles
  const styles = {
    footer: {
      background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
      color: 'white',
      marginTop: 'auto'
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 16px'
    },
    footerContent: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
      gap: '3rem',
      padding: '4rem 0 2rem'
    },
    footerSection: {
      display: 'flex',
      flexDirection: 'column'
    },
    footerLogo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '1rem'
    },
    logoIcon: {
      width: '48px',
      height: '48px',
      background: '#ffffff',
      borderRadius: '8px',
      objectFit: 'contain',
      padding: '3px'
    },
    footerLogoText: {
      display: 'flex',
      flexDirection: 'column'
    },
    footerLogoTitle: {
      color: 'white',
      fontSize: '1.25rem',
      fontWeight: 'bold',
      margin: '0 0 4px 0'
    },
    footerLogoSubtitle: {
      color: '#d1d5db',
      fontSize: '0.875rem',
      margin: 0
    },
    footerDescription: {
      color: '#d1d5db',
      lineHeight: '1.6',
      marginBottom: '1.5rem',
      fontSize: '0.95rem'
    },
    socialLinks: {
      display: 'flex',
      gap: '0.75rem'
    },
    socialLink: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      textDecoration: 'none',
      fontSize: '18px',
      transition: 'all 0.3s ease'
    },
    appSection: {
      marginTop: '1.5rem'
    },
    appTitle: {
      color: 'white',
      fontSize: '0.95rem',
      fontWeight: '600',
      margin: '0 0 0.75rem 0'
    },
    storeBadges: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.75rem',
      alignItems: 'center'
    },
    storeBadgeLink: {
      display: 'inline-flex',
      alignItems: 'center',
      textDecoration: 'none',
      transition: 'opacity 0.3s ease'
    },
    storeBadgeImg: {
      height: '40px',
      width: 'auto',
      display: 'block'
    },
    storeBadgeFallback: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      height: '40px',
      padding: '0 14px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      borderRadius: '8px',
      color: 'white',
      fontSize: '0.85rem',
      fontWeight: '500',
      whiteSpace: 'nowrap'
    },
    footerTitle: {
      color: 'white',
      fontSize: '1.125rem',
      fontWeight: '600',
      marginBottom: '1.5rem'
    },
    footerLinks: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    footerLinkItem: {
      marginBottom: '0.75rem'
    },
    footerLink: {
      color: '#d1d5db',
      textDecoration: 'none',
      transition: 'color 0.3s ease',
      fontSize: '0.95rem'
    },
    contactInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    contactItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem'
    },
    contactIcon: {
      fontSize: '18px',
      marginTop: '2px',
      flexShrink: 0
    },
    contactDetails: {
      display: 'flex',
      flexDirection: 'column'
    },
    contactLabel: {
      color: '#9ca3af',
      fontSize: '0.875rem',
      marginBottom: '2px'
    },
    contactValue: {
      color: 'white',
      fontWeight: '500',
      fontSize: '0.95rem'
    },
    footerBottom: {
      borderTop: '1px solid #374151',
      padding: '1.5rem 0'
    },
    footerBottomContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    copyright: {
      color: '#9ca3af',
      fontSize: '0.875rem'
    },
    footerBottomLinks: {
      display: 'flex',
      gap: '2rem'
    },
    footerBottomLink: {
      color: '#9ca3af',
      textDecoration: 'none',
      fontSize: '0.875rem',
      transition: 'color 0.3s ease'
    }
  };

  return (
    <footer style={styles.footer}>
      <div style={styles.container}>
        <div style={styles.footerContent}>
          {/* Company Info */}
          <div style={styles.footerSection}>
            <div style={styles.footerLogo}>
              <img src={process.env.PUBLIC_URL + '/logo192.png'} alt="Bestseed" style={styles.logoIcon} />
              <div style={styles.footerLogoText}>
                <h3 style={styles.footerLogoTitle}>Bestseed</h3>
                <p style={styles.footerLogoSubtitle}>Aquaculture Solutions</p>
              </div>
            </div>
            <p style={styles.footerDescription}>
              Your trusted partner in quality seeds and aquaculture solutions. 
              We provide premium seeds, expert guidance, and complete farming support.
            </p>
  <div style={styles.socialLinks}>
  <a
    href="https://www.instagram.com/bestseed.in/"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      ...styles.socialLink,
      color: "#E4405F" // Official Instagram brand color
    }}
  >
    <FaInstagram size={20} />
  </a>

  <a
    href="https://wa.me/919704756582"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      ...styles.socialLink,
      color: "#25D366" // Official WhatsApp brand color
    }}
  >
    <FaWhatsapp size={20} />
  </a>
</div>

            {/* Get the App */}
            <div style={styles.appSection}>
              <h4 style={styles.appTitle}>Get the App</h4>
              <div style={styles.storeBadges}>
                {STORE_LINKS.map((store) => (
                  <a
                    key={store.name}
                    href={store.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={store.label}
                    style={styles.storeBadgeLink}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <StoreBadge store={store} styles={styles} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div style={styles.footerSection}>
            <h4 style={styles.footerTitle}>Quick Links</h4>
            <ul style={styles.footerLinks}>
              <li style={styles.footerLinkItem}>
                <Link 
                  to="/products" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => e.target.style.color = '#0076BE'}
                  onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                >
                  Products
                </Link>
              </li>
              <li style={styles.footerLinkItem}>
                <Link 
                  to="/climate-news" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => e.target.style.color = '#0076BE'}
                  onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                >
                  Climate News
                </Link>
              </li>
              <li style={styles.footerLinkItem}>
                <Link 
                  to="/aqua-farming" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => e.target.style.color = '#0076BE'}
                  onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                >
                  Aqua Farming
                </Link>
              </li>
              <li style={styles.footerLinkItem}>
                <span
                  style={{ ...styles.footerLink, cursor: 'pointer' }}
                  onMouseEnter={(e) => e.target.style.color = '#0076BE'}
                  onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                >
                  Daily Prices
                </span>
              </li>
              <li style={styles.footerLinkItem}>
                <span
                  style={{ ...styles.footerLink, cursor: 'pointer' }}
                  onMouseEnter={(e) => e.target.style.color = '#0076BE'}
                  onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                >
                  Medicines
                </span>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div style={styles.footerSection}>
            <h4 style={styles.footerTitle}>Our Services</h4>
            <ul style={styles.footerLinks}>
              {['Seed Delivery', 'Farm Management', 'Aqua Consulting', 'Expert Support', 'Training Programs'].map((item, i) => (
                <li key={i} style={styles.footerLinkItem}>
                  <span
                    style={{ ...styles.footerLink, cursor: 'pointer' }}
                    onMouseEnter={(e) => e.target.style.color = '#0076BE'}
                    onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div style={styles.footerSection}>
            <h4 style={styles.footerTitle}>Contact Us</h4>
            <div style={styles.contactInfo}>
              <div style={styles.contactItem}>
                <span style={styles.contactIcon}>📞</span>
                <div style={styles.contactDetails}>
                  <div style={styles.contactLabel}>Phone</div>
                  <div style={styles.contactValue}>+91 97047 56582</div>
                </div>
              </div>
              <div style={styles.contactItem}>
                <span style={styles.contactIcon}>✉️</span>
                <div style={styles.contactDetails}>
                  <div style={styles.contactLabel}>Email</div>
                  <div style={styles.contactValue}>bestseed25@gmail.com</div>
                </div>
              </div>
              <div style={styles.contactItem}>
                <span style={styles.contactIcon}>📍</span>
                <div style={styles.contactDetails}>
                  <div style={styles.contactLabel}>Address</div>
                  <div style={styles.contactValue}>Cheyyuru, Dr. B.R. Ambedkar Konaseema, Andhra Pradesh 533222</div>
                </div>
              </div>
              <div style={styles.contactItem}>
                <span style={styles.contactIcon}>🕒</span>
                <div style={styles.contactDetails}>
                  <div style={styles.contactLabel}>Business Hours</div>
                  <div style={styles.contactValue}>Mon-Sun: 24/7</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div style={styles.footerBottom}>
          <div style={styles.footerBottomContent}>
            <div style={styles.copyright}>
              &copy; {currentYear} Bestseed. All rights reserved.
            </div>
            <div style={styles.footerBottomLinks}>
              <Link
                to="/privacy-policy"
                style={styles.footerBottomLink}
                onMouseEnter={(e) => e.target.style.color = '#0076BE'}
                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                style={styles.footerBottomLink}
                onMouseEnter={(e) => e.target.style.color = '#0076BE'}
                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
              >
                Terms of Service
              </Link>
              <Link
                to="/contact"
               style={styles.footerBottomLink}
                onMouseEnter={(e) => e.target.style.color = '#0076BE'}
                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
              >
                Contact Us
              </Link>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
                    alert('Account deletion request submitted. Our team will contact you shortly.');
                  }
                }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#ef4444', fontSize: '0.875rem',
                  transition: 'color 0.3s ease', padding: 0
                }}
                onMouseEnter={(e) => e.target.style.color = '#dc2626'}
                onMouseLeave={(e) => e.target.style.color = '#ef4444'}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;